import BookmarkAddOutlined from '@mui/icons-material/BookmarkAddOutlined'
import BoltOutlined from '@mui/icons-material/BoltOutlined'
import DeleteOutlined from '@mui/icons-material/DeleteOutlined'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import FormControlLabel from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { useState } from 'react'
import { describeError } from '../api/client'
import {
  useApplyTemplate,
  useDeleteTemplate,
  useSaveTemplateFromReport,
  useTemplates,
  type EntryTemplate,
  type TemplateModule,
} from '../api/templates'

const LABEL: Record<TemplateModule, string> = {
  WORK: 'work',
  LABOUR: 'labour',
  MATERIAL: 'material',
}

/**
 * Load a saved template, or keep today as one.
 *
 * <p>Sits above the entry form on every daily screen. Applying creates ordinary entries
 * and then steps aside — nothing stays linked to the template, so editing a template later
 * never reaches back into a day that has already been signed off.</p>
 */
export function TemplateBar({
  module,
  reportId,
  editable,
}: {
  module: TemplateModule
  reportId: string
  editable: boolean
}) {
  const templates = useTemplates(module, { dailyReportId: reportId })
  const apply = useApplyTemplate(reportId)
  const remove = useDeleteTemplate()
  const saveFromReport = useSaveTemplateFromReport()

  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const [saveOpen, setSaveOpen] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [overwrite, setOverwrite] = useState(false)
  const [notice, setNotice] = useState<{ severity: 'success' | 'warning'; text: string } | null>(null)
  const [error, setError] = useState<unknown>(null)

  const rows = templates.data ?? []

  async function runApply(template: EntryTemplate) {
    setAnchor(null)
    setError(null)
    setNotice(null)
    try {
      const result = await apply.mutateAsync(template.id)
      setNotice(
        result.skipped.length === 0
          ? { severity: 'success', text: `"${template.name}" added ${result.applied} line(s).` }
          : {
              severity: 'warning',
              text: `"${template.name}" added ${result.applied} line(s). Skipped: ${result.skipped.join('; ')}`,
            },
      )
    } catch (err) {
      setError(err)
    }
  }

  async function runSave() {
    setError(null)
    try {
      await saveFromReport.mutateAsync({
        dailyReportId: reportId,
        module,
        name: name.trim(),
        description: description.trim() || undefined,
        overwrite,
      })
      setSaveOpen(false)
      setName('')
      setDescription('')
      setOverwrite(false)
      setNotice({ severity: 'success', text: 'Saved. It will be in the template list tomorrow.' })
    } catch (err) {
      setError(err)
    }
  }

  if (!editable) {
    return null
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          alignItems: 'center',
          flexWrap: 'wrap',
          mb: 2,
        }}
      >
        <Button
          size="small"
          variant="outlined"
          startIcon={<BoltOutlined />}
          disabled={rows.length === 0 || apply.isPending}
          onClick={(e) => setAnchor(e.currentTarget)}
        >
          {apply.isPending ? 'Applying...' : `Use a template${rows.length ? ` (${rows.length})` : ''}`}
        </Button>
        <Button
          size="small"
          startIcon={<BookmarkAddOutlined />}
          onClick={() => {
            setError(null)
            setSaveOpen(true)
          }}
        >
          Save today as template
        </Button>
        {rows.length === 0 && !templates.isLoading && (
          <Typography variant="caption" color="text.secondary">
            No {LABEL[module]} templates for this site yet — enter a normal day, then save it.
          </Typography>
        )}
      </Box>

      {notice && (
        <Alert severity={notice.severity} sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice.text}
        </Alert>
      )}
      {Boolean(error) && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {describeError(error)}
        </Alert>
      )}

      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {rows.map((template) => (
          <MenuItem key={template.id} onClick={() => void runApply(template)} sx={{ maxWidth: 460 }}>
            <ListItemText
              primary={template.name}
              secondary={
                <>
                  {template.summary}
                  {template.useCount > 0 && ` · used ${template.useCount}×`}
                </>
              }
              slotProps={{
                primary: { sx: { fontWeight: 600 } },
                secondary: { sx: { whiteSpace: 'normal' } },
              }}
            />
            <Tooltip title="Delete this template">
              <IconButton
                size="small"
                edge="end"
                sx={{ ml: 2 }}
                onClick={(e) => {
                  e.stopPropagation()
                  void remove.mutateAsync(template.id)
                }}
              >
                <DeleteOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </MenuItem>
        ))}
        {rows.length > 0 && <Divider />}
        <MenuItem disabled sx={{ opacity: 0.7 }}>
          <Typography variant="caption">
            Applying fills in the plan for this day. Enter what actually happened against it.
          </Typography>
        </MenuItem>
      </Menu>

      <Dialog open={saveOpen} onClose={() => setSaveOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Save today's {LABEL[module]} as a template</DialogTitle>
        <DialogContent>
          {Boolean(error) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {describeError(error)}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The planned figures on this report are captured for future days at this site —
            planned quantities, expected headcounts, ordered quantities. What actually
            happened today stays with today.
          </Typography>
          <TextField
            label="Template name"
            fullWidth
            autoFocus
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Block A day gang"
            sx={{ mb: 2 }}
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            minRows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <FormControlLabel
            sx={{ mt: 1 }}
            control={<Switch checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} />}
            label="Replace an existing template with this name"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSaveOpen(false)} disabled={saveFromReport.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={saveFromReport.isPending || name.trim().length < 2}
            onClick={() => void runSave()}
          >
            {saveFromReport.isPending ? 'Saving...' : 'Save template'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
